import { ArrowRight, Rocket, Shield, Users, Wand2 } from 'lucide-react';
import { FadeIn } from '../components/AnimatedPanel';
import { HeroBadge } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

interface HomePanelProps {
  onAuth: (mode: 'login' | 'register') => void;
  onNavigate: (panel: string) => void;
}

export function HomePanel({ onAuth, onNavigate }: HomePanelProps) {
  const { user } = useAuth();

  return (
    <div className="space-y-8">
      <FadeIn>
        <HeroBadge />
        <h1 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-tight md:text-5xl">
          Build worlds.{' '}
          <span className="bg-gradient-to-r from-korvex-accent to-korvex-violet bg-clip-text text-transparent">
            Play together.
          </span>
        </h1>
        <p className="mt-4 max-w-xl text-lg text-korvex-muted">
          Korvex is a next-gen UGC game platform on playkorvex.com — create models, publish games, and
          connect with creators worldwide.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {user ? (
            <button type="button" className="btn-primary" onClick={() => onNavigate('discover')}>
              Explore games
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <>
              <button type="button" className="btn-primary" onClick={() => onAuth('register')}>
                Create account
              </button>
              <button type="button" className="btn-ghost" onClick={() => onAuth('login')}>
                Sign in
              </button>
            </>
          )}
        </div>
      </FadeIn>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          {
            icon: Wand2,
            title: 'Creator tools',
            desc: 'Studio-powered workflows for models and games — inventory coming soon.',
          },
          {
            icon: Users,
            title: 'Social play',
            desc: 'Friend other players, visit profiles, and grow your creator circle.',
          },
          {
            icon: Shield,
            title: 'Safe community',
            desc: 'Unique usernames, moderation tools, and platform admin oversight.',
          },
        ].map(({ icon: Icon, title, desc }, i) => (
          <FadeIn key={title} delay={0.1 + i * 0.08}>
            <div className="panel-card h-full">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-korvex-accent/15 text-korvex-accent">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-korvex-muted">{desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={0.35}>
        <div className="panel-card flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-korvex-violet to-korvex-pink">
              <Rocket className="h-7 w-7 text-white" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold">Coming soon</h3>
              <p className="text-sm text-korvex-muted">
                Models inventory, published games library, and Korvex Studio integration.
              </p>
            </div>
          </div>
          <span className="rounded-full border border-korvex-violet/40 bg-korvex-violet/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-korvex-violet">
            Early access
          </span>
        </div>
      </FadeIn>
    </div>
  );
}
